import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  TrademarkDeclareSingleComponent,
} from './trademark-declare-single/trademark-declare-single.component';
import {
  TrademarkDeclareImportComponent,
} from './trademark-declare-import/trademark-declare-import.component';
import { MessageService } from 'src/app/services/message.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import {
  ExternalCreateTrademarkResult,
  TrademarkService,
} from 'src/app/services/trademark.service';
import {
  Trademark,
  TrademarkDTO,
} from 'src/app/models/trademark';
import { Location } from '@angular/common';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import {
  TrademarkExternalCreateDialogComponent,
} from 'src/app/dialogs/trademark-external-create-dialog/trademark-external-create-dialog.component';
import * as _ from 'lodash';

@Component({
  selector: 'app-trademark-declare',
  templateUrl: './trademark-declare.component.html',
  styleUrls: ['./trademark-declare.component.scss']
})
export class TrademarkDeclareComponent implements OnInit {

  mode: "SEARCH" | "IMPORT" | "CREATE" = "SEARCH";

  @ViewChild("declareSingle") declareSingle?: TrademarkDeclareSingleComponent;
  @ViewChild("declareImport") declareImport?: TrademarkDeclareImportComponent;

  private loading: boolean = false;

  trademark: Trademark = {
    _id: undefined,
    identifierNumber: '',
    registrationNumber: undefined,
    applicationNumber: undefined,
    countryOfDesignation: '',
    kind: undefined,
    type: undefined,
    niceClassification: [],
    applicationDate: undefined,
    publicationDate: undefined,
    registrationDate: undefined,
    expirationDate: undefined,
    reproductionURI: undefined,
    name: undefined,
    organizationId: undefined
  };

  reproduction?: File;

  constructor(
    private _message: MessageService,
    private dialog: MatDialog,
    private location: Location,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private trademarkService: TrademarkService,
    private translate: TranslateService
  ) {
  }

  ngOnInit(): void {
  }

  back() {
    this.router.navigate([".."], { relativeTo: this.route })
  }

  setMode(mode: "SEARCH" | "IMPORT" | "CREATE") {
    this.mode = mode;
  }

  onReproductionChange(reproduction: any) {
    this.reproduction = reproduction;
    if (!this.reproduction && this.trademark) {
      this.trademark.reproductionURI = undefined;
    }
  }

  canDeclare() {
    return !this.loading && ((this.mode === 'SEARCH' && this.declareSingle && this.declareSingle.selection.selected.length > 0)
      || (this.mode === 'IMPORT' && this.declareImport && this.declareImport.selection.selected.length > 0))
      || (this.mode === 'CREATE' && this.isTrademarkValid());
  }

  isTrademarkValid() {
    return this.trademark.niceClassification && this.trademark.niceClassification.length > 0
      && (this.trademark.registrationNumber || this.trademark.applicationNumber) && this.trademark.countryOfDesignation && this.trademark.propertyOffice && this.trademark.type
      && (!!this.trademark.registrationDate || !!this.trademark.applicationDate)
  }

  _isCircular(d: any) {
    try { JSON.stringify(d) }
    catch (e) { return true }
    return false
  }

  async declare() {
    if (['SEARCH', 'IMPORT'].includes(this.mode)) {
      let trademarks: TrademarkDTO[] = [];
      if (this.mode === 'SEARCH' && this.declareSingle && this.declareSingle.selection.selected.length > 0) {
        trademarks = this.declareSingle.getTrademarksFromSelection().filter(trademark => !this._isCircular(trademark));
        trademarks.forEach((trademark: TrademarkDTO) => {
          if (this.declareSingle && this.declareSingle.familyMode && ['create', 'select'].includes(this.declareSingle.familyMode)) {
            trademark.family = {
              mode: this.declareSingle.familyMode,
              ref: this.declareSingle.family?._id
            }
          }
        });
      } else if (this.mode === 'IMPORT' && this.declareImport && this.declareImport.selection.selected.length > 0) {
        trademarks = this.declareImport.getTrademarksFromSelection().filter(trademark => !this._isCircular(trademark));
      }
      if (trademarks.length > 0) {
        let toast;
        try {
          this.loading = true;
          this._message.emitChange("LOADING", "START");
          toast = this.toastr.info(
            this.translate.instant(
              (trademarks.length > 1 ? 'TRADEMARK_DECLARE.DECLARE_MULTIPLE_IN_PROGRESS_MESSAGE' : 'TRADEMARK_DECLARE.DECLARE_IN_PROGRESS_MESSAGE'),
              { length: trademarks.length }
            ),
            (trademarks.length > 1 ? 'TRADEMARK_DECLARE.DECLARE_MULTIPLE_IN_PROGRESS_TITLE' : 'TRADEMARK_DECLARE.DECLARE_IN_PROGRESS_TITLE'),
            {
              progressBar: true,
              disableTimeOut: true
            }
          )
          const chunks = _.chunk(trademarks, 1000);
          const errors: ExternalCreateTrademarkResult[] = [];
          const declared: ExternalCreateTrademarkResult[] = [];
          for (const chunk of chunks) {
            try {
              const res = await this.trademarkService.createExternal(chunk);
              declared.push(...res.declared)
              if (res.errors) {
                errors.push(...res.errors)
              }
            } catch (err: any) {
              console.log(err)
              errors.push(...chunk.map(tm => ({ trademark: tm, ok: false, error: "INTERNAL_ERROR" })))
            }
          }
          this.toastr.clear(toast.toastId);
          this.loading = false;
          this._message.emitChange("LOADING", "END");
          const hasErrors = errors.length > 0;
          if (hasErrors) {
            const message = trademarks.length === 1 ? this.translate.instant('TRADEMARK_DECLARE.TRADEMARK_DECLARED_WITH_ERROR') : this.translate.instant('TRADEMARK_DECLARE.TRADEMARK_DECLARED_WITH_ERRORS', { count: errors.length })
            const toast = this.toastr.error(message, undefined, {
              disableTimeOut: true
            });
            toast.onTap.subscribe((res) => {
              this.declarationReport({ declared, errors });
            })
          } else {
            const message = trademarks.length === 1 ? this.translate.instant('TRADEMARK_DECLARE.TRADEMARK_DECLARED', { name: declared[0].trademark.name || declared[0].trademark.identifierNumber }) : this.translate.instant('TRADEMARK_DECLARE.TRADEMARKS_DECLARED', { count: declared.length })
            const toast = this.toastr.success(message);
            toast.onTap.subscribe((res) => {
              this.declarationReport({ declared, errors });
            })
            this.location.back();
          }

        } catch (err: any) {
          console.log(err)
          if (toast) {
            this.toastr.clear(toast.toastId);
          }
          this.loading = false;
          this._message.emitChange("LOADING", "END");
          this.toastr.error(`ERRORS.GENERIC`);
        }
      }
    } else {
      if (this.isTrademarkValid()) {
        try {
          this.loading = true;
          this._message.emitChange("LOADING", "START");
          if (this.trademark.registrationNumber !== undefined || this.trademark.applicationNumber !== undefined)
            this.trademark.identifierNumber = this.trademark.registrationNumber !== undefined ? this.trademark.registrationNumber : this.trademark.applicationNumber!;
          if (this.reproduction) {
            const upload = await this.trademarkService.uploadReproduction(this.reproduction);
            this.trademark.reproductionURI = upload.url;
          }
          await this.trademarkService.create(this.trademark);
          this.loading = false;
          this._message.emitChange("LOADING", "END");
          this.toastr.success(this.translate.instant('TRADEMARK_DECLARE.TRADEMARK_DECLARED', { name: this.trademark.name || this.trademark.identifierNumber }));
          this.location.back();
        } catch (err: any) {
          this.loading = false;
          this._message.emitChange("LOADING", "END");
          if (err && err.status === 403 && err.error.message === "MAX_TRADEMARKS_EXCEEDED") {
            this.toastr.error(`ORGANIZATION.MAX_TRADEMARKS_EXCEEDED`);
          } else {
            this.toastr.error(`ERRORS.GENERIC`);
          }
        }
      }

    }
  }

  declarationReport(results: { declared: ExternalCreateTrademarkResult[], errors: ExternalCreateTrademarkResult[] }) {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '1200px',
      maxHeight: '600px',
      autoFocus: false,
      data: {
        results
      }
    }
    const dialog = this.dialog.open(TrademarkExternalCreateDialogComponent, config);
  }


}
